import { useCallback, useEffect, useRef } from "react";
import { TimerUpdateSource, EventStatusEvent } from "@swiggy-private/cart-types";
// utils
import { isEmpty } from "@swiggy-private/js-utils";
import { isValidNumber } from "@swiggy-private/core-ui";
// apis
import { fetchEventStatusPublic } from "@cart-client/apis/publicCart";

interface UseIPLSwiggySixesPollingProps {
    validateAndSetTimestamp: (inputTimeStampInSec?: number, source?: TimerUpdateSource) => void;
    isEnabled: boolean;
}

const POLLING_INTERVAL = 30000;

export const useIPLSwiggySixesPolling = ({ validateAndSetTimestamp, isEnabled }: UseIPLSwiggySixesPollingProps) => {
    const pollingIntervalRef = useRef<NodeJS.Timeout>();
    const validateAndSetTimestampRef = useRef(validateAndSetTimestamp);

    useEffect(() => {
        validateAndSetTimestampRef.current = validateAndSetTimestamp;
    }, [validateAndSetTimestamp]);

    const fetchEventStatus = useCallback(async () => {
        try {
            const eventStatusData = await fetchEventStatusPublic();
            if (!isEmpty(eventStatusData?.data) && !isEmpty(eventStatusData.data.events)) {
                const iplEvent = eventStatusData.data.events.find(
                    (event: EventStatusEvent) => event.event_name === "FLOATY_TYPE_IPL",
                );

                if (
                    iplEvent?.live &&
                    isValidNumber(iplEvent.offer_end_time_seconds) &&
                    iplEvent.offer_end_time_seconds > 0
                ) {
                    validateAndSetTimestampRef.current(
                        iplEvent.offer_end_time_seconds,
                        TimerUpdateSource.EVENT_STATUS_POLLING,
                    );
                }
            }
        } catch (err) {
            // noop
        }
    }, []);

    const startPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            return;
        }

        fetchEventStatus();

        pollingIntervalRef.current = setInterval(() => {
            fetchEventStatus();
        }, POLLING_INTERVAL);
    }, [fetchEventStatus]);

    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = undefined;
        }
    }, []);

    useEffect(() => {
        if (isEnabled) {
            startPolling();
        } else {
            stopPolling();
        }

        return () => {
            stopPolling();
        };
    }, [isEnabled]);

    return {
        stopPolling,
    };
};
